import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { AssignmentTable, type ApiAssignment } from '@/components/AssignmentTable';
import { apiFetch } from '@/lib/http';
import { dbg } from '@/lib/dbg';

interface SimpleDetailsDialogProps {
  cycleId: number;
  triggerLabel?: string;
}

export function SimpleDetailsDialog({ cycleId, triggerLabel }: SimpleDetailsDialogProps) {
  if (!cycleId) {
    return null;
  }

  // Use the exact same query pattern as Sign-Off v2
  const { data: printSnapshot, isLoading, error } = useQuery<{ assignments: ApiAssignment[] }>({
    queryKey: ['/api/locked-cycles', cycleId, 'pdf-data'],
    queryFn: async () => {
      dbg.log('[SimpleDetailsDialog] fetching assignments', { cycleId });
      const res = await apiFetch(`/api/locked-cycles/${cycleId}/pdf-data`);
      if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
      const data = await res.json();
      dbg.log('[SimpleDetailsDialog] received assignments payload', {
        cycleId,
        assignmentCount: data?.assignments?.length ?? 0,
      });
      return data;
    },
    enabled: !!cycleId,
  });

  const handleDialogOpen = () => {
    dbg.log('Cycle Snapshots → Details dialog opened', { cycleId });
  };

  return (
    <Dialog onOpenChange={(open) => open && handleDialogOpen()}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Eye className="h-3 w-3 mr-1" /> {triggerLabel ?? 'Preview Snapshot'}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Cycle Snapshot Details - Cycle {cycleId}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            This cycle snapshot contains assignments frozen at the time the cycle was locked.
          </p>

          {isLoading && (
            <div className="text-sm text-gray-600">Loading assignments...</div>
          )}

          {error && (
            <div className="text-sm text-red-600">
              Error loading assignments: {error instanceof Error ? error.message : 'Unknown error'}
            </div>
          )}

          {printSnapshot?.assignments && (
            <div className="mt-4">
              <p className="text-sm font-medium mb-4">
                Showing {printSnapshot.assignments.length} assignments from frozen data
              </p>
              <AssignmentTable assignments={printSnapshot.assignments} />
            </div>
          )}

          {!isLoading && !error && !printSnapshot?.assignments && (
            <div className="text-sm text-gray-600">No assignments in this snapshot.</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
